import { useEffect, useMemo, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { NumberFormatBase } from 'react-number-format';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import endpoints from '../../config/endpoints';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import { useAppSelector } from '../../services/store';
import { Sidebar } from '../Layout/Sidebar';
import { PickableUser, UserPicker } from '../shared/UserPicker';

type SortKey = 'sku' | 'title' | 'price';
type SortDirection = 'asc' | 'desc';
type PageTab = 'base' | 'lists';

interface PriceListSummary {
  id: number;
  title: string;
  itemCount: number;
  userCount: number;
}

const itemsPerPage = 25;

const sortIndicator = (active: boolean, direction: SortDirection) => (active ? (direction === 'asc' ? ' ▲' : ' ▼') : '');

const sameSet = (a: Set<number>, b: Set<number>) => a.size === b.size && Array.from(a).every(id => b.has(id));

// "لیست قیمت" has two tabs:
//   1. قیمت پایه محصولات — the original fast bulk editor for Product.price
//      itself (the catalog default), unrelated to any specific list.
//   2. لیست‌های قیمتی — named price lists (عمده‌فروشی، VIP، ...), each with
//      its own per-product prices and its own assigned customers; a customer
//      only ever sees/pays their assigned list's prices (a per-user
//      Access.price override still wins over the list if one is set — see
//      src/services/pricing.service.ts on the backend).
const PriceList = () => {
  const dispatch = useDispatch();
  const userReducer = useAppSelector(state => state.userReducer);
  const [pageTab, setPageTab] = useState<PageTab>('lists');

  const [products, setProducts] = useState<any[]>([]);

  // ─── Tab 1: base product prices ────────────────────────────────────────
  const [baseEdits, setBaseEdits] = useState<Record<number, string>>({});
  const [baseQuery, setBaseQuery] = useState('');
  const [baseSortKey, setBaseSortKey] = useState<SortKey>('title');
  const [baseSortDirection, setBaseSortDirection] = useState<SortDirection>('asc');
  const [baseOffset, setBaseOffset] = useState(0);
  const [savingBase, setSavingBase] = useState(false);

  // ─── Tab 2: price lists ─────────────────────────────────────────────────
  const [priceLists, setPriceLists] = useState<PriceListSummary[]>([]);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [listPriceByProductId, setListPriceByProductId] = useState<Record<number, number>>({});
  const [listEdits, setListEdits] = useState<Record<number, string>>({});
  const [listQuery, setListQuery] = useState('');
  const [listSortKey, setListSortKey] = useState<SortKey>('title');
  const [listSortDirection, setListSortDirection] = useState<SortDirection>('asc');
  const [listOffset, setListOffset] = useState(0);
  const [savingList, setSavingList] = useState(false);

  const [allUsers, setAllUsers] = useState<PickableUser[]>([]);
  const [assignedUserIds, setAssignedUserIds] = useState<Set<number>>(new Set());
  const [originalUserIds, setOriginalUserIds] = useState<Set<number>>(new Set());

  const selectedList = priceLists.find(l => l.id === selectedListId) || null;

  const fetchPriceLists = async (selectId?: number) => {
    const res = await restApi(endpoints.priceList.index, true).get();
    if (res.code === 200) {
      const lists: PriceListSummary[] = res.data || [];
      setPriceLists(lists);
      if (selectId !== undefined) {
        setSelectedListId(selectId);
      } else if (selectedListId === null && lists.length > 0) {
        setSelectedListId(lists[0].id);
      }
    }
  };

  const fetchProducts = async () => {
    const res = await restApi(endpoints.product.index, true).get();
    if (res.code === 200) setProducts(res.data || []);
  };

  const fetchAllUsers = async () => {
    const res = await restApi(endpoints.sms.users, true).get();
    if (res.code === 200) setAllUsers(res.data || []);
  };

  const fetchListDetail = async (listId: number) => {
    dispatch(setLoading(true));
    const [itemsRes, usersRes] = await Promise.all([
      restApi(endpoints.priceList.items(listId), true).get(),
      restApi(endpoints.priceList.users(listId), true).get(),
    ]);
    if (itemsRes.code === 200) {
      const byId: Record<number, number> = {};
      (itemsRes.data || []).forEach((item: any) => { byId[item.productId] = item.price; });
      setListPriceByProductId(byId);
    }
    if (usersRes.code === 200) {
      const ids = new Set<number>((usersRes.data || []).map((u: any) => u.id));
      setAssignedUserIds(ids);
      setOriginalUserIds(ids);
    }
    setListEdits({});
    dispatch(setLoading(false));
  };

  useEffect(() => {
    dispatch(setLoading(true));
    Promise.all([fetchPriceLists(), fetchProducts(), fetchAllUsers()]).finally(() => dispatch(setLoading(false)));
  }, []);

  useEffect(() => {
    if (selectedListId !== null) fetchListDetail(selectedListId);
  }, [selectedListId]);

  const roleScopedProducts = useMemo(() => {
    return products.filter(p => userReducer.profile?.role === 'FELTBOWLCOMMERCE' ? p.category?.slug === 'کاسه-نمد' : true);
  }, [products, userReducer.profile?.role]);

  // ─── List CRUD ──────────────────────────────────────────────────────────

  const createList = async () => {
    const result = await Swal.fire({
      title: 'لیست قیمت جدید',
      input: 'text',
      inputLabel: 'عنوان لیست',
      inputPlaceholder: 'مثلا: عمده‌فروشی',
      showCancelButton: true,
      confirmButtonText: 'ایجاد',
      cancelButtonText: 'انصراف',
    });
    if (!result.isConfirmed || !result.value?.trim()) return;

    const res = await restApi(endpoints.priceList.basic, true).post({ title: result.value.trim() });
    if (res.code === 200) {
      await fetchPriceLists(res.data.id);
    } else {
      Swal.fire({ title: 'ناموفق', text: res?.data || 'خطا در ایجاد لیست', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  const renameList = async () => {
    if (!selectedList) return;
    const result = await Swal.fire({
      title: 'ویرایش نام لیست',
      input: 'text',
      inputValue: selectedList.title,
      showCancelButton: true,
      confirmButtonText: 'ذخیره',
      cancelButtonText: 'انصراف',
    });
    if (!result.isConfirmed || !result.value?.trim()) return;

    const res = await restApi(endpoints.priceList.basic + selectedList.id, true).post({ title: result.value.trim() });
    if (res.code === 200) {
      await fetchPriceLists(selectedList.id);
    } else {
      Swal.fire({ title: 'ناموفق', text: res?.data || 'خطا در ویرایش لیست', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  const deleteList = async () => {
    if (!selectedList) return;
    const confirm = await Swal.fire({
      title: 'حذف لیست قیمت',
      text: `لیست «${selectedList.title}» حذف می‌شود و ${selectedList.userCount} کاربر متصل به آن، بدون لیست قیمت می‌مانند. ادامه می‌دهید؟`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'بله، حذف کن',
      cancelButtonText: 'انصراف',
    });
    if (!confirm.isConfirmed) return;

    const res = await restApi(endpoints.priceList.delete(selectedList.id), true).delete({});
    if (res.code === 200) {
      setSelectedListId(null);
      await fetchPriceLists();
    } else {
      Swal.fire({ title: 'ناموفق', text: res?.data || 'خطا در حذف لیست', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  // ─── Tab 2: one combined save (members + prices) ───────────────────────

  const listEditedCount = Object.keys(listEdits).length;
  const usersChanged = !sameSet(assignedUserIds, originalUserIds);
  const listHasChanges = listEditedCount > 0 || usersChanged;

  const saveList = async () => {
    if (!selectedListId || !listHasChanges) return;
    setSavingList(true);

    const requests: Promise<any>[] = [];
    if (usersChanged) {
      requests.push(restApi(endpoints.priceList.users(selectedListId), true).post({ userIds: Array.from(assignedUserIds) }));
    }
    if (listEditedCount > 0) {
      const payload = Object.keys(listEdits).map((id) => ({ id: Number(id), price: listEdits[Number(id)] }));
      requests.push(restApi(endpoints.priceList.prices(selectedListId), true).post({ products: payload }));
    }
    const results = await Promise.all(requests);
    setSavingList(false);

    const failed = results.find(r => r.code !== 200);
    if (!failed) {
      Swal.fire({ title: 'موفق', text: 'تغییرات این لیست ذخیره شد', icon: 'success', confirmButtonText: 'متوجه شدم' });
      await fetchListDetail(selectedListId);
      await fetchPriceLists(selectedListId);
    } else {
      Swal.fire({ title: 'ناموفق', text: failed?.data || 'خطا در ذخیره تغییرات', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  const discardListChanges = () => {
    setListEdits({});
    setAssignedUserIds(originalUserIds);
  };

  const visibleListProducts = useMemo(() => {
    return roleScopedProducts.filter(p =>
      p.sku?.toLowerCase().includes(listQuery.toLowerCase()) ||
      p.title?.toLowerCase().includes(listQuery.toLowerCase())
    );
  }, [roleScopedProducts, listQuery]);

  const sortedListProducts = useMemo(() => {
    const dir = listSortDirection === 'asc' ? 1 : -1;
    return [...visibleListProducts].sort((a, b) => {
      const aVal = listSortKey === 'price' ? (listPriceByProductId[a.id] ?? a.price) : a[listSortKey];
      const bVal = listSortKey === 'price' ? (listPriceByProductId[b.id] ?? b.price) : b[listSortKey];
      if (typeof aVal === 'string' || typeof bVal === 'string') {
        return dir * String(aVal || '').localeCompare(String(bVal || ''));
      }
      return dir * ((aVal || 0) - (bVal || 0));
    });
  }, [visibleListProducts, listSortKey, listSortDirection, listPriceByProductId]);

  const listPageCount = Math.ceil(sortedListProducts.length / itemsPerPage);
  const currentListItems = sortedListProducts.slice(listOffset, listOffset + itemsPerPage);

  const toggleListSort = (key: SortKey) => {
    if (key === listSortKey) setListSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else { setListSortKey(key); setListSortDirection('asc'); }
  };

  const setListEdit = (productId: number, value: string) => {
    setListEdits(prev => ({ ...prev, [productId]: value }));
  };

  // Blank means "not overridden in this list" (falls back to Product.price
  // at resolution time) — not the same as an explicit 0.
  const valueForList = (product: any) => {
    const edited = listEdits[product.id];
    if (edited !== undefined) return edited;
    return listPriceByProductId[product.id] ?? '';
  };

  const listTh = (key: SortKey, label: string) => (
    <th className="clickable" onClick={() => toggleListSort(key)}>
      {label}{sortIndicator(listSortKey === key, listSortDirection)}
    </th>
  );

  // ─── Tab 1: base product prices ─────────────────────────────────────────

  const baseEditedCount = Object.keys(baseEdits).length;

  const visibleBaseProducts = useMemo(() => {
    return roleScopedProducts.filter(p =>
      p.sku?.toLowerCase().includes(baseQuery.toLowerCase()) ||
      p.title?.toLowerCase().includes(baseQuery.toLowerCase())
    );
  }, [roleScopedProducts, baseQuery]);

  const sortedBaseProducts = useMemo(() => {
    const dir = baseSortDirection === 'asc' ? 1 : -1;
    return [...visibleBaseProducts].sort((a, b) => {
      const aVal = a[baseSortKey];
      const bVal = b[baseSortKey];
      if (typeof aVal === 'string' || typeof bVal === 'string') {
        return dir * String(aVal || '').localeCompare(String(bVal || ''));
      }
      return dir * ((aVal || 0) - (bVal || 0));
    });
  }, [visibleBaseProducts, baseSortKey, baseSortDirection]);

  const basePageCount = Math.ceil(sortedBaseProducts.length / itemsPerPage);
  const currentBaseItems = sortedBaseProducts.slice(baseOffset, baseOffset + itemsPerPage);

  const toggleBaseSort = (key: SortKey) => {
    if (key === baseSortKey) setBaseSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else { setBaseSortKey(key); setBaseSortDirection('asc'); }
  };

  const setBaseEdit = (productId: number, value: string) => {
    setBaseEdits(prev => ({ ...prev, [productId]: value }));
  };

  const valueForBase = (product: any) => {
    const edited = baseEdits[product.id];
    return edited !== undefined ? edited : (product.price ?? '');
  };

  const discardBaseChanges = () => setBaseEdits({});

  const saveBaseChanges = async () => {
    if (baseEditedCount === 0) return;
    const payload = Object.keys(baseEdits).map((id) => ({ id: Number(id), price: baseEdits[Number(id)] }));

    setSavingBase(true);
    const res = await restApi(endpoints.product.prices, true).post({ products: payload });
    setSavingBase(false);

    if (res.code === 200) {
      const updatedById = new Map<number, any>((res.data || []).map((p: any): [number, any] => [p.id, p]));
      setProducts(prev => prev.map(p => (updatedById.has(p.id) ? { ...p, ...(updatedById.get(p.id) || {}) } : p)));
      setBaseEdits({});
      Swal.fire({ title: 'موفق', text: `قیمت پایه ${payload.length} محصول بروزرسانی شد`, icon: 'success', confirmButtonText: 'متوجه شدم' });
    } else {
      Swal.fire({ title: 'ناموفق', text: res?.data || 'خطا در بروزرسانی قیمت‌ها', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  const baseTh = (key: SortKey, label: string) => (
    <th className="clickable" onClick={() => toggleBaseSort(key)}>
      {label}{sortIndicator(baseSortKey === key, baseSortDirection)}
    </th>
  );

  return (
    <>
      <div className="dashboardBody">
        <Sidebar />
        <main className="dashBoardMain">
          <h1 className="dashBoardTitle">لیست قیمت</h1>

          <div className="dashTabs inventory">
            <span className={`ordersTag inventory ${pageTab === 'base' ? 'active' : ''}`} onClick={() => setPageTab('base')}>
              قیمت پایه محصولات
            </span>
            <span className={`ordersTag inventory ${pageTab === 'lists' ? 'active' : ''}`} onClick={() => setPageTab('lists')}>
              لیست‌های قیمتی
            </span>
          </div>

          {pageTab === 'base' && (
            <>
              <div className="searchContainer" style={{ marginTop: '16px' }}>
                <div className="buttonContainer keepRight">
                  <span
                    className={`dashboardHeader keepRight ${baseEditedCount === 0 || savingBase ? 'disabled' : 'clickable'}`}
                    onClick={saveBaseChanges}
                  >
                    <p>{savingBase ? 'در حال ذخیره...' : `ذخیره تغییرات${baseEditedCount ? ` (${baseEditedCount})` : ''}`}</p>
                  </span>
                  {baseEditedCount > 0 && !savingBase && (
                    <span className="secondaryBtn clickable" onClick={discardBaseChanges}><p>انصراف از تغییرات</p></span>
                  )}
                </div>
                <div className="dashboardseaechBox">
                  <i className="dashMagnifierIcon"></i>
                  <input
                    className="dashSearchInput"
                    placeholder="جستجو بر اساس نام یا SKU"
                    onChange={(input) => setBaseQuery(input.target.value)}
                  />
                </div>
              </div>

              <section className="shelf-management-section">
                <div className="cardex-table-container">
                  <table className="cardex-table">
                    <thead>
                    <tr className="cardex-table-header">
                      {baseTh('sku', 'SKU')}
                      {baseTh('title', 'نام محصول')}
                      {baseTh('price', 'قیمت پایه')}
                    </tr>
                    </thead>
                    <tbody>
                    {currentBaseItems.map((product) => {
                      const isEdited = baseEdits[product.id] !== undefined;
                      return (
                        <tr className={`cardex-row ${isEdited ? 'bgGreen' : ''}`} key={product.id}>
                          <td className="cardex-cell">{product.sku}</td>
                          <td className="cardex-cell">{product.title}</td>
                          <td className="cardex-cell">
                            <NumberFormatBase
                              className="inventoryInput"
                              value={valueForBase(product)}
                              onValueChange={(values) => setBaseEdit(product.id, values.value)}
                              format={(str) => (str ? tools.formatPrice(Number(str)) : '')}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    {currentBaseItems.length === 0 && (
                      <tr><td className="cardex-cell" colSpan={3}>محصولی یافت نشد</td></tr>
                    )}
                    </tbody>
                  </table>
                </div>

                <ReactPaginate
                  breakLabel="..."
                  nextLabel="بعدی >"
                  onPageChange={(event) => setBaseOffset((event.selected * itemsPerPage) % (sortedBaseProducts.length || 1))}
                  pageRangeDisplayed={5}
                  pageCount={basePageCount}
                  previousLabel="< قبلی"
                  renderOnZeroPageCount={null}
                  className="pagination"
                  pageClassName="paginationBreak"
                  previousClassName="paginationBreak"
                  nextClassName="paginationBreak"
                  activeClassName="paginationActive"
                />
              </section>
            </>
          )}

          {pageTab === 'lists' && (
            <>
              <div className="addInfoHeader" style={{ marginTop: '16px' }}>
                <div className="buttonContainer keepRight">
                  <span className="dashboardHeader keepRight clickable" onClick={createList}>
                    <p>+ لیست جدید</p>
                  </span>
                </div>
              </div>

              <div className="dashTabs inventory">
                {priceLists.map(list => (
                  <span
                    key={list.id}
                    className={`ordersTag inventory ${selectedListId === list.id ? 'active' : ''}`}
                    onClick={() => setSelectedListId(list.id)}
                  >
                    {list.title} ({list.itemCount})
                  </span>
                ))}
                {priceLists.length === 0 && <span className="sideBarTitle">هنوز لیست قیمتی ایجاد نشده</span>}
              </div>

              {selectedList && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <span className="secondaryBtn clickable" onClick={renameList}><p>ویرایش نام</p></span>
                      <span className="secondaryBtn clickable" onClick={deleteList}><p>حذف لیست</p></span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {listHasChanges && !savingList && (
                        <span className="secondaryBtn clickable" onClick={discardListChanges}><p>انصراف از تغییرات</p></span>
                      )}
                      <span
                        className={`dashboardHeader ${!listHasChanges || savingList ? 'disabled' : 'clickable'}`}
                        onClick={saveList}
                      >
                        <p>{savingList ? 'در حال ذخیره...' : 'ثبت تغییرات این لیست'}</p>
                      </span>
                    </div>
                  </div>

                  {/* Users assigned to this list */}
                  <section className="shelf-management-section">
                    <div className="shelf-management-header">
                      <h3 className="shelf-management-title">کاربران این لیست ({assignedUserIds.size})</h3>
                    </div>
                    <UserPicker users={allUsers} selectedIds={assignedUserIds} setSelectedIds={setAssignedUserIds} />
                  </section>

                  {/* Product price grid for the selected list */}
                  <div className="searchContainer" style={{ marginTop: '20px' }}>
                    <div className="dashboardseaechBox">
                      <i className="dashMagnifierIcon"></i>
                      <input
                        className="dashSearchInput"
                        placeholder="جستجو بر اساس نام یا SKU"
                        onChange={(input) => setListQuery(input.target.value)}
                      />
                    </div>
                  </div>

                  <section className="shelf-management-section">
                    <div className="cardex-table-container">
                      <table className="cardex-table">
                        <thead>
                        <tr className="cardex-table-header">
                          {listTh('sku', 'SKU')}
                          {listTh('title', 'نام محصول')}
                          {listTh('price', `قیمت در «${selectedList.title}»`)}
                        </tr>
                        </thead>
                        <tbody>
                        {currentListItems.map((product) => {
                          const isEdited = listEdits[product.id] !== undefined;
                          return (
                            <tr className={`cardex-row ${isEdited ? 'bgGreen' : ''}`} key={product.id}>
                              <td className="cardex-cell">{product.sku}</td>
                              <td className="cardex-cell">{product.title}</td>
                              <td className="cardex-cell">
                                <NumberFormatBase
                                  className="inventoryInput"
                                  placeholder={product.price ? tools.formatPrice(product.price) : ''}
                                  value={valueForList(product)}
                                  onValueChange={(values) => setListEdit(product.id, values.value)}
                                  format={(str) => (str ? tools.formatPrice(Number(str)) : '')}
                                />
                              </td>
                            </tr>
                          );
                        })}
                        {currentListItems.length === 0 && (
                          <tr><td className="cardex-cell" colSpan={3}>محصولی یافت نشد</td></tr>
                        )}
                        </tbody>
                      </table>
                    </div>

                    <ReactPaginate
                      breakLabel="..."
                      nextLabel="بعدی >"
                      onPageChange={(event) => setListOffset((event.selected * itemsPerPage) % (sortedListProducts.length || 1))}
                      pageRangeDisplayed={5}
                      pageCount={listPageCount}
                      previousLabel="< قبلی"
                      renderOnZeroPageCount={null}
                      className="pagination"
                      pageClassName="paginationBreak"
                      previousClassName="paginationBreak"
                      nextClassName="paginationBreak"
                      activeClassName="paginationActive"
                    />
                  </section>
                </>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default PriceList;
